import React, { useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  VStack,
  HStack,
  Box,
} from "@chakra-ui/react";
import { saveKGColumn } from "../../../services/DataModelService";
import { Header } from "build-ts/metadata";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { 
  ColumnType, 
  columnTypeToJSON, 
  columnTypeFromJSON,
  TimeBucket,
  timeBucketToJSON,
  timeBucketFromJSON,
  AggregationType,
  aggregationTypeToJSON,
  aggregationTypeFromJSON
} from "build-ts/enums";
import showToast from "hooks/useCustomToast";

interface CreateKGColumnComponentProps {
  onClose: () => void;
  colType: ColumnType;
  refreshKGColumns: () => void;
}

const CreateKGColumnComponent: React.FC<CreateKGColumnComponentProps> = ({
  onClose,
  colType,
  refreshKGColumns
}) => {
  const [kgColumnName, setKGColumnName] = useState("");
  const [kgColumnDescription, setKGColumnDescription] = useState("");
  const [columnType, setColumnType] = useState<ColumnType>(colType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardinality, setCardinality] = useState<number | undefined>(undefined);
  const [timeBucket, setTimeBucket] = useState<TimeBucket>(TimeBucket.DAILY);
  const [aggregation, setAggregation] = useState<AggregationType>(AggregationType.SUM);

  const handleSubmit = async () => {
    if (!kgColumnName.trim()) {
      showToast("Name is required", "", "error");
      return;
    }
    setIsSubmitting(true);
    
    const kgColumnProto = KGColumnProto.create({
      header: Header.create({
        name: kgColumnName,
        description: kgColumnDescription
      }),
      column_type: columnType,
    });
    
    // Add properties based on column type
    if (columnType === ColumnType.ATTRIBUTE && cardinality !== undefined) {
      kgColumnProto.cardinality = cardinality;
    } else if (columnType === ColumnType.MEASURE) {
      kgColumnProto.aggregation = aggregation;
    } else if (columnType === ColumnType.TEMPORAL) {
      kgColumnProto.time_bucket = timeBucket;
    }
    
    const result = await saveKGColumn(kgColumnProto, false);
    if (result) {
      await refreshKGColumns();
      //onSuccess(result);
      showToast("KG Column created successfully", "", "success");
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>KG Column Name</FormLabel>
          <Input
            placeholder="Enter KG column name"
            value={kgColumnName}
            onChange={(e) => setKGColumnName(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            placeholder="Enter description"
            value={kgColumnDescription}
            onChange={(e) => setKGColumnDescription(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Column Type</FormLabel>
          <Select
            value={columnTypeToJSON(columnType)}
            onChange={(e) => setColumnType(columnTypeFromJSON(e.target.value))}
          >
            <option value={columnTypeToJSON(ColumnType.ATTRIBUTE)}>ATTRIBUTE</option>
            <option value={columnTypeToJSON(ColumnType.MEASURE)}>MEASURE</option>
            <option value={columnTypeToJSON(ColumnType.TEMPORAL)}>TEMPORAL</option>
          </Select>
        </FormControl>
        
        {/* Conditional fields based on column type */}
        {columnType === ColumnType.ATTRIBUTE && (
          <FormControl>
            <FormLabel>Cardinality</FormLabel>
            <NumberInput min={0} value={cardinality ?? ""}>
              <NumberInputField 
                placeholder="Enter cardinality" 
                onChange={(e) => setCardinality(e.target.value ? Number(e.target.value) : undefined)}
              />
            </NumberInput>
          </FormControl>
        )}
        
        {columnType === ColumnType.MEASURE && (
          <FormControl>
            <FormLabel>Default Aggregation</FormLabel>
            <Select
              value={aggregationTypeToJSON(aggregation)}
              onChange={(e) => setAggregation(aggregationTypeFromJSON(e.target.value))}
            >
              <option value={aggregationTypeToJSON(AggregationType.SUM)}>SUM</option>
              <option value={aggregationTypeToJSON(AggregationType.AVERAGE)}>AVERAGE</option>
              <option value={aggregationTypeToJSON(AggregationType.COUNT)}>COUNT</option>
              <option value={aggregationTypeToJSON(AggregationType.UNIQUE_COUNT)}>UNIQUE_COUNT</option>
              <option value={aggregationTypeToJSON(AggregationType.GROWTH)}>GROWTH</option>
              <option value={aggregationTypeToJSON(AggregationType.MIN)}>MIN</option>
              <option value={aggregationTypeToJSON(AggregationType.MAX)}>MAX</option>
            </Select>
          </FormControl>
        )}
        
        {columnType === ColumnType.TEMPORAL && (
          <FormControl>
            <FormLabel>Time Bucket</FormLabel>
            <Select
              value={timeBucketToJSON(timeBucket)}
              onChange={(e) => setTimeBucket(timeBucketFromJSON(e.target.value))}
            >
              <option value={timeBucketToJSON(TimeBucket.DAILY)}>DAILY</option>
              <option value={timeBucketToJSON(TimeBucket.WEEKLY)}>WEEKLY</option>
              <option value={timeBucketToJSON(TimeBucket.MONTHLY)}>MONTHLY</option>
              <option value={timeBucketToJSON(TimeBucket.QUARTERLY)}>QUARTERLY</option>
              <option value={timeBucketToJSON(TimeBucket.YEARLY)}>YEARLY</option>
            </Select>
          </FormControl>
        )}

        <HStack spacing={3} pt={4}>
          <Button 
            colorScheme="purple" 
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            Create
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default CreateKGColumnComponent; 